import QRCode from 'qrcode'
import { QRWithLogoOptions } from 'Types/QrCode.d'
import axios from './axios'

export const processDataInChunks = (rows: string[][], headers: string[], setLoadingProgress: (progress: number) => void) => {
  const CHUNK_SIZE = 1000
  const totalRows = rows.length
  let processedRows = 0
  let allData: any[] = []
  
  return new Promise<any[]>((resolve) => {
    function processChunk() {
      const chunkEnd = Math.min(processedRows + CHUNK_SIZE, totalRows)
      
      for (let i = processedRows; i < chunkEnd; i++) {
        const row = rows[i]
        const rowData: any = { key: i - 1 } // Adjust for header row
        headers.forEach((header, j) => {
          rowData[header] = row[j]
        })
        allData.push(rowData)
      }
      
      processedRows = chunkEnd
      const progress = Math.floor((processedRows / totalRows) * 100)
      setLoadingProgress(progress)
      
      if (processedRows < totalRows) {
        setTimeout(processChunk, 0) // Use setTimeout to prevent UI blocking
      } else {
        resolve(allData)
      }
    }
    
    processChunk()
  })
}

export const extractSheetInfo = (url: string) => {
  const sheetIdMatch = url.match(/\/d\/([a-zA-Z0-9-_]+)/)
  const sheetId = sheetIdMatch?.[1]
  
  if (!sheetId) {
    throw new Error('Invalid Google Sheet URL. Expected format: https://docs.google.com/spreadsheets/d/YOUR_SHEET_ID/...')
  }
  
  const gidMatch = url.match(/[#&]gid=([0-9]+)/)
  const gid = gidMatch?.[1] || '0'
  
  return {
    sheetId,
    gid,
    csvUrl: `https://docs.google.com/spreadsheets/d/${sheetId}/export?format=csv&gid=${gid}`
  }
}

// Auto-detect the most likely delimiter
export const detectDelimiter = (csvText: string): string => {
  const sample = csvText.split('\n').slice(0, 5).join('\n') // Use first 5 lines as sample
  const delimiters = [',', '\t', ';', '|']
  let bestDelimiter = ','
  let maxColumns = 0
  
  for (const delimiter of delimiters) {
    const lines = sample.split('\n').filter(line => line.trim())
    if (lines.length === 0) continue
    
    const columnCounts = lines.map(line => {
      // Simple count for detection (not full parsing)
      return line.split(delimiter).length
    })
    
    // Check consistency - good delimiter should have consistent column counts
    const avgColumns = columnCounts.reduce((a, b) => a + b, 0) / columnCounts.length
    const isConsistent = columnCounts.every(count => Math.abs(count - avgColumns) <= 1)
    
    if (isConsistent && avgColumns > maxColumns) {
      maxColumns = avgColumns
      bestDelimiter = delimiter
    }
  }
  
  console.log(`Auto-detected delimiter: "${bestDelimiter}" (avg ${maxColumns} columns)`)
  return bestDelimiter
}

// Enhanced CSV parser that properly handles special characters
export const parseCSVLine = (line: string): string[] => {
  const result: string[] = []
  let current = ''
  let inQuotes = false
  let i = 0

  while (i < line.length) {
    const char = line[i]
    const nextChar = line[i + 1]

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote inside quoted field
        current += '"'
        i += 2
        continue
      } else {
        // Toggle quote state
        inQuotes = !inQuotes
        i++
        continue
      }
    }

    if (char === ',' && !inQuotes) {
      // End of field
      result.push(current.trim())
      current = ''
      i++
      continue
    }

    // Regular character
    current += char
    i++
  }

  // Add the last field
  result.push(current.trim())
  return result
}

export const parseCsvData = (csvText: string) => {
  if (!csvText || csvText.trim() === '') {
    throw new Error('The sheet returned empty data. Please check if the sheet has data and proper permissions.')
  }

  // Check if the response is an HTML error page instead of CSV
  if (csvText.includes('<!DOCTYPE html>') || csvText.includes('<html>')) {
    throw new Error('Received HTML instead of CSV data. The sheet might require authentication or have restricted access.')
  }
  
  try {
    // Auto-detect the best delimiter
    const delimiter = detectDelimiter(csvText)
    
    // Split lines but handle potential multiline fields
    const lines = csvText.split('\n').filter(line => line.trim() !== '')
    const rows: string[][] = []
    
    for (const line of lines) {
      if (delimiter === ',') {
        // Use enhanced CSV parser for comma-separated values (handles quotes properly)
        const parsedRow = parseCSVLine(line)
        rows.push(parsedRow)
      } else {
        // For other delimiters, use simple splitting (less common to have special chars)
        const parsedRow = line.split(delimiter).map(cell => 
          cell.replace(/^"|"$/g, '').trim()
        )
        rows.push(parsedRow)
      }
    }
    
    // Additional validation
    if (rows.length > 0) {
      const expectedColumns = rows[0].length
      const validRows = rows.filter(row => row.length === expectedColumns)
      
      if (validRows.length < rows.length * 0.8) { // If more than 20% of rows are invalid
        throw new Error(`Inconsistent data structure detected. Expected ${expectedColumns} columns but found varying counts.`)
      }
    }
    
    return rows
    
  } catch (error) {
    console.error('Enhanced CSV parsing failed, falling back to simple parsing:', error)
    
    // Fallback to simple parsing
    const rows = csvText.split('\n')
      .filter(line => line.trim() !== '')
      .map(row => {
        const delimiter = row.includes(',') ? ',' : '\t'
        return row.split(delimiter).map(cell => cell.replace(/^"|"$/g, '').trim())
      })
    
    return rows
  }
}

export const validateSheetHeaders = (headers: string[]) => {
  const productUrlColumn = headers.find(header => 
    header.toLowerCase().includes('url') || header.toLowerCase().includes('link web')
  )
  
  if (!productUrlColumn) {
    throw new Error('Google Sheet must contain columns for product URL and SKU/ID. These columns are required for generating QR codes.')
  }
  
  return { productUrlColumn }
}

export const findSheetColumns = (data: any[]) => {
  if (!data.length) return {}
  
  const urlColumn = Object.keys(data[0]).find(key => 
    key.toLowerCase().includes('url') || key.toLowerCase().includes('link')
  )
  
  const skuColumn = Object.keys(data[0]).find(key => 
    key.toLowerCase().includes('sku') || 
    key.toLowerCase() === 'id' || 
    key.toLowerCase() === 'product_id'
  )
  
  const nameColumn = Object.keys(data[0]).find(key => 
    key.toLowerCase().includes('name') || 
    key.toLowerCase().includes('title') ||
    key.toLowerCase().includes('tên') || 
    key.toLowerCase() === 'tên sp' ||
    key.toLowerCase() === 'ten sp' ||
    key.toLowerCase() === 'ten san pham' ||
    key.toLowerCase() === 'tên sản phẩm'
  )
  
  return { urlColumn, skuColumn, nameColumn }
}

export const addUtmParameters = (baseUrl: string, utmSource: string, utmMedium: string, utmCampaign: string) => {
  const urlObj = new URL(baseUrl)
  if (utmSource) urlObj.searchParams.append('utm_source', utmSource)
  if (utmMedium) urlObj.searchParams.append('utm_medium', utmMedium)
  if (utmCampaign) urlObj.searchParams.append('utm_campaign', utmCampaign)
  return urlObj.toString()
}

export const shortenUrl = async (url: string, productName: string, kuttApiKey: string, kuttDomain: string): Promise<string> => {
  try {
    const baseUrl = kuttDomain.trim().endsWith('/') ? kuttDomain.trim().slice(0, -1) : kuttDomain.trim()
    const linksEndpoint = `${baseUrl}/api/v2/links`
    
    const response = await axios({
      method: 'POST',
      url: linksEndpoint,
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': kuttApiKey,
        'Accept': 'application/json'
      },
      data: {
        target: url,
        description: productName ? `QR cho sản phẩm: ${productName}` : `Generated by QR Code Generator tool`,
        domain: 'r.kingfoodmart.com'
      }
    })
    
    let shortLink = response.data.link || url
    if (shortLink.startsWith('http://')) {
      shortLink = shortLink.replace(/^http:\/\//, 'https://')
    }
    return shortLink
    
  } catch (error: any) {
    console.error('Error shortening URL with Kutt:', error)
    throw new Error(`Could not shorten URL with Kutt: ${error.message || 'Unknown error'}`)
  }
}

export const loadImage = (src: string): Promise<HTMLImageElement | null> => {
  return new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = "anonymous"
    img.onload = () => resolve(img)
    img.onerror = () => resolve(null)
    img.src = src
    
    setTimeout(() => {
      if (!img.complete) {
        resolve(null)
      }
    }, 3000)
  })
}

export const generateQRWithLogo = async (text: string, options: QRWithLogoOptions, logoSizePercent: number): Promise<string> => {
  // Tính kích thước logo dựa trên tỉ lệ phần trăm của options.width
  const calculatedLogoSize = Math.round((logoSizePercent / 100) * options.width)
  
  // First generate the QR code without logo
  const qrCodeDataUrl = await QRCode.toDataURL(text, {
    color: options.color,
    width: options.width,
    margin: options.margin,
    errorCorrectionLevel: 'L'
  })
  
  // If no logo provided, return the QR code as is
  if (!options.logoImage) {
    return qrCodeDataUrl
  }
  
  try {
    // Create canvas to combine QR code and logo
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      const qrImage = new Image()
      
      qrImage.onload = () => {
        // Set canvas size to match QR code
        canvas.width = qrImage.width
        canvas.height = qrImage.height
        
        // Make the background transparent
        if (ctx) {
          ctx.clearRect(0, 0, canvas.width, canvas.height)
        } else {
          resolve(qrCodeDataUrl)
          return
        }
        
        // Draw QR code on canvas
        ctx.drawImage(qrImage, 0, 0)
        
        // Make white parts transparent if needed (for black on transparent)
        if (options.color.dark === '#000000') {
          // Get the image data
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
          const data = imageData.data
          
          // Loop through each pixel
          for (let i = 0; i < data.length; i += 4) {
            // If the pixel is white (R=255, G=255, B=255)
            if (data[i] === 255 && data[i + 1] === 255 && data[i + 2] === 255) {
              // Set alpha channel to 0 (fully transparent)
              data[i + 3] = 0
            }
          }
          
          // Put the modified image data back on the canvas
          ctx.putImageData(imageData, 0, 0)
        }
        
        if (options.logoImage) {
          // Sử dụng kích thước logo đã tính
          const logoWidth = calculatedLogoSize
          const logoHeight = calculatedLogoSize
          
          // Calculate center position
          const centerX = (canvas.width - logoWidth) / 2
          const centerY = (canvas.height - logoHeight) / 2
          
          // Calculate margin based on the set margin ratio
          const logoMargin = logoWidth * (10 / 100) // Convert percentage to decimal
          const radius = logoWidth / 2 + logoMargin // Radius of the circular cutout
          
          // Create circular clipping path for clearing QR code behind logo
          ctx.save() // Save the current state
          ctx.beginPath()
          ctx.arc(
            centerX + logoWidth / 2, 
            centerY + logoHeight / 2, 
            radius, 
            0, 
            Math.PI * 2, 
            false
          )
          ctx.closePath()
          
          // Clear the circular area (make it transparent)
          ctx.globalCompositeOperation = 'destination-out'
          ctx.fill()
          
          // Restore normal drawing mode
          ctx.globalCompositeOperation = 'source-over'
          
          // Draw logo on top of transparent area
          ctx.drawImage(
            options.logoImage,
            centerX,
            centerY,
            logoWidth,
            logoHeight
          )
          
          ctx.restore() // Restore the state to remove the clipping path
        }
        
        // Convert canvas to data URL with transparency
        const dataUrl = canvas.toDataURL('image/png')
        resolve(dataUrl)
      }
      
      qrImage.onerror = () => {
        console.error("Error loading QR code image")
        resolve(qrCodeDataUrl)
      }
      
      qrImage.src = qrCodeDataUrl
    })
  } catch (error) {
    console.error("Error in generateQRWithLogo:", error)
    return qrCodeDataUrl
  }
}

export const dataURLtoBlob = (dataURL: string): Blob => {
  const arr = dataURL.split(',')
  const mime = arr[0].match(/:(.*?);/)![1]
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  return new Blob([u8arr], { type: mime })
}

export const createCsvContent = (data: any[]) => {
  return [
    ['SKU', 'Product Name', 'QR Code URL (with UTM)'].join(','),
    ...data.map(item => [
      item.sku,
      item.product_name ? `"${item.product_name}"` : '',
      `"${item.product_url}"`
    ].join(','))
  ].join('\n')
}

// Test function for CSV parsing with special characters
export const testCsvParsing = () => {
  console.log('🧪 Testing CSV parsing with special characters...')
  
  const testCases = [
    {
      name: 'Normal CSV',
      csv: 'Name,SKU,URL\nProduct A,SKU001,https://example.com\nProduct B,SKU002,https://example2.com',
      expected: 3
    },
    {
      name: 'CSV with commas in quoted fields',
      csv: '"Product A, Red Color","SKU001","https://example.com"\n"Product B, Blue Color","SKU002","https://example2.com"',
      expected: 3
    },
    {
      name: 'CSV with escaped quotes',
      csv: '"Product ""Premium"" A","SKU001","https://example.com"\n"Product ""Basic"" B","SKU002","https://example2.com"',
      expected: 3
    },
    {
      name: 'Mixed special characters',
      csv: '"Sản phẩm A, màu đỏ (size L)","SKU001","https://example.com?utm_source=test&utm_medium=qr"\n"Sản phẩm ""cao cấp"", giá tốt","SKU002","https://example2.com"',
      expected: 3
    }
  ]
  
  testCases.forEach(testCase => {
    try {
      const rows = parseCsvData(testCase.csv)
      const allRowsHaveExpectedColumns = rows.every(row => row.length === testCase.expected)
      
      if (allRowsHaveExpectedColumns) {
        console.log(`✅ ${testCase.name}: PASSED`)
        console.log(`   Sample parsed: [${rows[0]?.join(', ')}]`)
      } else {
        console.log(`❌ ${testCase.name}: FAILED - Inconsistent column counts`)
        console.log(`   Expected: ${testCase.expected}, Got: ${rows.map(r => r.length)}`)
      }
    } catch (error) {
      console.log(`❌ ${testCase.name}: ERROR - ${error}`)
    }
  })
  
  console.log('🧪 CSV parsing tests completed!')
}

// Uncomment the line below to run tests in development
// testCsvParsing() 